/**
 * New-chart draft helpers (W2-14 onboarding-wizard; DESIGN.md §4.3, §5.1). Pure functions over the contract's
 * `NewChartDraft`: the starting draft, the song-select preview range defaults and checks, the difficulty label,
 * and title/artist guesses from a file name.
 *
 * Preview range (DESIGN.md §5.1 step 4, contract ChartMeta.previewStart): start = min(45 s, max(0, duration - 16 s)),
 * end = start + 15 s, clamped to duration - 0.5 s. Phira refuses to open a chart whose preview ends after the music
 * (checker PH-36 keeps the same 0.5 s margin), so the wizard never lets a range outside the song through.
 */

import type { NewChartDraft, WizardStepId } from '../../app/registry/types';

/** Tempo and first beat used until the Beat step confirms them (DESIGN.md §4.3 "Skip -> 120 BPM / offset 0"). */
export const DEFAULT_BPM = 120;
/** Songs shorter than this cannot hold a valid song-select preview range. */
export const MIN_SONG_SEC = 2;
/** Default song-select preview start and length (seconds). */
export const PREVIEW_DEFAULT_START_SEC = 45;
export const PREVIEW_LENGTH_SEC = 15;
/** The preview must end at least this long before the decoded song ends (browser MP3 decoder padding). */
export const PREVIEW_END_MARGIN_SEC = 0.5;
/** Room kept after the default preview start (the default start is at most duration - 16 s). */
export const PREVIEW_TAIL_SEC = 16;
/** Difficulty number range and step (info.yml `difficulty`, Phira's UI shows 0..20). */
export const DIFFICULTY_MIN = 0;
export const DIFFICULTY_MAX = 20;
export const DIFFICULTY_STEP = 0.1;
/** Difficulty labels offered as buttons; anything else is a custom label. */
export const LEVEL_LABELS = ['EZ', 'HD', 'IN', 'AT', 'SP'] as const;
export type LevelLabel = (typeof LEVEL_LABELS)[number];
/** Default difficulty of a new chart at Full level (DESIGN.md §4.3 wireframe "IN Lv.13"). */
export const DEFAULT_LEVEL_LABEL: LevelLabel = 'IN';
export const DEFAULT_DIFFICULTY = 13;
/** At Guided level a first chart starts as an easy one (UX-074). */
export const GUIDED_LEVEL_LABEL: LevelLabel = 'EZ';
/** A typical difficulty number of each label (Phigros-like ranges); a label change proposes it. */
export const TYPICAL_DIFFICULTY: Readonly<Record<LevelLabel, number>> = { EZ: 3, HD: 8, IN: 13, AT: 15, SP: 13 };

/**
 * The difficulty number after the label changed from `from` to `to`: the new label's typical value while the number
 * is still the old label's typical value (the user did not type one), else the number unchanged.
 */
export function difficultyForLabel(from: string, to: string, difficulty: number): number {
  const typical = (l: string): number | undefined =>
    (LEVEL_LABELS as readonly string[]).includes(l) ? TYPICAL_DIFFICULTY[l as LevelLabel] : undefined;
  const was = typical(from);
  const next = typical(to);
  if (next === undefined) return difficulty;
  return was === undefined || Math.abs(difficulty - was) < 1e-9 ? next : difficulty;
}
/** Longest title, artist, illustrator or charter name the wizard accepts. */
export const MAX_TEXT_LENGTH = 200;
/** Settings key of the remembered charter name (`settings.ext`, Settings page row). */
export const CHARTER_EXT_KEY = 'wizard.charter';

/** Seconds rounded to milliseconds (fields show three decimals at most). */
export function roundMs(sec: number): number {
  return Math.round(sec * 1000) / 1000;
}

/** The empty draft every wizard run starts from (`guided`: an easy first chart, EZ Lv.3). */
export function initialDraft(opts: { readonly charter: string; readonly guided?: boolean }): NewChartDraft {
  const label = opts.guided === true ? GUIDED_LEVEL_LABEL : DEFAULT_LEVEL_LABEL;
  return {
    music: null,
    illustration: null,
    name: '',
    composer: '',
    illustrator: '',
    charter: opts.charter,
    levelLabel: label,
    difficulty: opts.guided === true ? TYPICAL_DIFFICULTY[label] : DEFAULT_DIFFICULTY,
    bpm: DEFAULT_BPM,
    offsetMs: 0,
    timingConfirmed: false,
    start: 'oneLine',
    previewStart: 0,
    previewEnd: PREVIEW_LENGTH_SEC,
    analysis: null,
    skipped: [],
  };
}

/** The latest end a preview range may have for a song of `durationSec`. */
export function latestPreviewEnd(durationSec: number): number {
  // Rounded down to milliseconds: never past the margin.
  return Math.floor(Math.max(0, durationSec - PREVIEW_END_MARGIN_SEC) * 1000 + 1e-6) / 1000;
}

/** Default song-select preview range for a song of `durationSec` seconds (always inside the song when it is long enough). */
export function defaultPreviewRange(durationSec: number): { readonly start: number; readonly end: number } {
  const d = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0;
  const start = roundMs(Math.min(PREVIEW_DEFAULT_START_SEC, Math.max(0, d - PREVIEW_TAIL_SEC)));
  const end = roundMs(Math.min(start + PREVIEW_LENGTH_SEC, latestPreviewEnd(d)));
  return { start, end };
}

/**
 * Keeps a chosen range inside a (new) song: an end past the song is pulled back, a start that no longer fits moves
 * so that the range keeps its length where possible. Falls back to the default range when nothing fits.
 */
export function fitPreviewRange(
  start: number,
  end: number,
  durationSec: number,
): { readonly start: number; readonly end: number } {
  const last = latestPreviewEnd(durationSec);
  if (!(Number.isFinite(start) && Number.isFinite(end)) || end <= start || last <= 0) {
    return defaultPreviewRange(durationSec);
  }
  let s: number;
  let e: number;
  if (start >= 0 && end <= last) {
    s = roundMs(start);
    e = Math.min(last, roundMs(end));
  } else {
    const length = Math.min(end - start, last);
    s = roundMs(Math.max(0, Math.min(start, last - length)));
    e = Math.min(last, roundMs(s + length));
  }
  return e > s ? { start: s, end: e } : defaultPreviewRange(durationSec);
}

/** What is wrong with a preview range for a song of `durationSec` seconds, or null when it is valid. */
export type PreviewRangeProblem = 'negative' | 'order' | 'beyondSong' | 'notFinite';

export function previewRangeProblem(start: number, end: number, durationSec: number): PreviewRangeProblem | null {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 'notFinite';
  if (start < 0) return 'negative';
  if (end <= start) return 'order';
  if (durationSec > 0 && end > latestPreviewEnd(durationSec) + 1e-9) return 'beyondSong';
  return null;
}

/** Difficulty number clamped to 0..20 and rounded to 0.1. */
export function cleanDifficulty(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_DIFFICULTY;
  const clamped = Math.min(DIFFICULTY_MAX, Math.max(DIFFICULTY_MIN, n));
  return Math.round(clamped * 10) / 10;
}

/** "13" for whole numbers, "13.5" otherwise (one decimal). */
export function formatDifficulty(n: number): string {
  const d = cleanDifficulty(n);
  return Number.isInteger(d) ? String(d) : d.toFixed(1);
}

/** The level string written to the chart ("IN Lv.13"); a blank label gives "Lv.13". */
export function formatLevel(label: string, difficulty: number): string {
  const l = label.trim();
  const lv = `Lv.${formatDifficulty(difficulty)}`;
  return l === '' ? lv : `${l} ${lv}`;
}

export function isPresetLabel(label: string): label is LevelLabel {
  return (LEVEL_LABELS as readonly string[]).includes(label);
}

/** File name without folders. */
export function baseName(name: string): string {
  const parts = name.split(/[\\/]/);
  return parts[parts.length - 1] ?? name;
}

/** Lower-case extension without the dot ('' when there is none). */
export function extOf(name: string): string {
  const base = baseName(name);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

/** File name without folders and extension. */
export function stem(name: string): string {
  const base = baseName(name);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? base : base.slice(0, dot);
}

/**
 * Title and artist guessed from a file name: "Artist - Title.mp3" gives both, anything else only the title.
 * Underscores count as spaces; a leading track number ("03 - ", "03. ") is dropped.
 */
export function guessFromFileName(name: string): { readonly title: string; readonly artist: string } {
  let s = stem(name).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  s = s.replace(/^\d{1,3}\s*[-.)]\s+/, '');
  const dash = s.split(/\s+[-\u2013\u2014]\s+/);
  if (dash.length >= 2) {
    const artist = (dash[0] ?? '').trim();
    const title = dash.slice(1).join(' - ').trim();
    if (artist !== '' && title !== '') return { title, artist };
  }
  return { title: s, artist: '' };
}

/** Text trimmed and cut to the accepted length. */
export function cleanText(s: string): string {
  return s.trim().slice(0, MAX_TEXT_LENGTH);
}

/** Adds or removes a step in the skipped list (keeps the list ordered as given by `order`). */
export function withSkipped(skipped: readonly WizardStepId[], id: WizardStepId, on: boolean): readonly WizardStepId[] {
  const has = skipped.includes(id);
  if (on === has) return skipped;
  return on ? [...skipped, id] : skipped.filter((s) => s !== id);
}

/** True when the tempo of a draft is usable (finite and > 0). */
export function validBpm(bpm: number): boolean {
  return Number.isFinite(bpm) && bpm > 0;
}

/** "3:12" for 192 s (minutes and seconds, used next to the song name). */
export function formatDuration(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}
